import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Package, Search, Plus, Download, AlertCircle, FileText, Settings as SettingsIcon, FileDown, ClipboardCheck, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import EditAssetDialog from "@/components/EditAssetDialog";

export default function AssetsDashboard() {
  const [searchTerm, setSearchTerm] = useState("");
  const [assets, setAssets] = useState<any[]>([]);
  const [loans, setLoans] = useState<any[]>([]);
  const [loanSettings, setLoanSettings] = useState<any>(null);
  const [audits, setAudits] = useState<any[]>([]);
  const [customLocations, setCustomLocations] = useState<string[]>([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [showCustomLocation, setShowCustomLocation] = useState(false);
  const [showCustomCategory, setShowCustomCategory] = useState(false);
  const [addAssetDialogOpen, setAddAssetDialogOpen] = useState(false);
  const [loanDialogOpen, setLoanDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<any>(null);
  const [ptas, setPtas] = useState<any[]>([]);
  const [auditSettings, setAuditSettings] = useState(false);
  const [trusteePolicies, setTrusteePolicies] = useState<any>(null);
  const [editAssetDialogOpen, setEditAssetDialogOpen] = useState(false);
  const [assetToEdit, setAssetToEdit] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [assetToDelete, setAssetToDelete] = useState<any>(null);
  const [filterLocation, setFilterLocation] = useState<string>("all");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [filterLoanable, setFilterLoanable] = useState<string>("all");
  const [ptaSearchTerm, setPtaSearchTerm] = useState("");
  const { toast } = useToast();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: "",
    category: "",
    custom_category: "",
    location: "",
    custom_location: "",
    value: "",
    description: "",
    loanable: false
  });

  const [loanFormData, setLoanFormData] = useState({
    borrower_type: "individual",
    borrower_pta_id: "",
    borrower_pta_name: "",
    borrower_first_name: "",
    borrower_last_name: "",
    borrower_email: "",
    borrower_phone: "",
    loan_fee: 0,
    due_date: ""
  });

  useEffect(() => {
    if (user) {
      fetchAssets();
      fetchLoans();
      fetchLoanSettings();
      fetchCustomLocations();
      fetchPtas();
      fetchAudits();
      fetchTrusteePolicies();
    }
  }, [user]);

  const fetchAssets = async () => {
    const { data } = await supabase
      .from('assets')
      .select('*')
      .order('created_at', { ascending: false });
    setAssets(data || []);
  };

  const fetchLoans = async () => {
    const { data } = await supabase
      .from('asset_loans')
      .select('*, assets(*)')
      .order('created_at', { ascending: false });
    setLoans(data || []);
  };

  const fetchLoanSettings = async () => {
    const { data: member } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .eq('is_pta_admin', true)
      .single();

    if (member?.pta_id) {
      const { data } = await supabase
        .from('asset_loan_settings')
        .select('*')
        .eq('pta_id', member.pta_id)
        .single();
      
      setLoanSettings(data || {
        loaning_enabled: true,
        stripe_enabled: false,
        bacs_enabled: false,
        cheque_enabled: false,
        cash_enabled: false
      });
    }
  };

  const fetchCustomLocations = async () => {
    const { data: member } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .eq('is_pta_admin', true)
      .single();

    if (member?.pta_id) {
      const { data } = await supabase
        .from('asset_custom_locations')
        .select('location_name')
        .eq('pta_id', member.pta_id);
      
      setCustomLocations(data?.map(l => l.location_name) || []);
    }
  };

  const fetchPtas = async () => {
    const { data } = await supabase
      .from('ptas')
      .select('id, name, school_name')
      .order('name');
    setPtas(data || []);
  };

  const fetchTrusteePolicies = async () => {
    const { data } = await supabase
      .from('trustee_policies')
      .select('*')
      .single();
    
    if (data) {
      setTrusteePolicies(data);
      // If trustee forces audits on, enable audit settings
      if (data.force_asset_audit) {
        setAuditSettings(true);
      }
    }
  };

  const fetchAudits = async () => {
    const { data: member } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .eq('is_pta_admin', true)
      .single();

    if (member?.pta_id) {
      const { data } = await supabase
        .from('asset_audits')
        .select('*')
        .eq('pta_id', member.pta_id)
        .order('audit_date', { ascending: false });
      
      setAudits(data || []);
    }
  };

  const handleAddAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { data: member } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .eq('is_pta_admin', true)
      .single();

    if (!member?.pta_id) {
      toast({
        title: "Error",
        description: "You must be a PTA admin to add assets",
        variant: "destructive"
      });
      return;
    }

    const location = formData.location === "other" ? formData.custom_location : formData.location;
    const category = formData.category === "other" ? formData.custom_category : formData.category;

    // Save custom location if new
    if (formData.location === "other" && formData.custom_location && !customLocations.includes(formData.custom_location)) {
      await supabase
        .from('asset_custom_locations')
        .insert({
          pta_id: member.pta_id,
          location_name: formData.custom_location
        });
      setCustomLocations([...customLocations, formData.custom_location]);
    }

    const { error } = await supabase
      .from('assets')
      .insert({
        pta_id: member.pta_id,
        name: formData.name,
        category: category,
        custom_category: formData.category === "other" ? formData.custom_category : null,
        location: location,
        value: formData.value ? parseFloat(formData.value) : null,
        description: formData.description,
        loanable: formData.loanable
      });

    if (error) {
      toast({
        title: "Error adding asset",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Asset added",
        description: "The asset has been added to the catalogue.",
      });
      setAddAssetDialogOpen(false);
      setFormData({
        name: "",
        category: "",
        custom_category: "",
        location: "",
        custom_location: "",
        value: "",
        description: "",
        loanable: false
      });
      fetchAssets();
    }
  };

  const handleCreateLoan = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedAsset) return;

    const { data: member } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .eq('is_pta_admin', true)
      .single();

    if (!member?.pta_id) return;

    const { error } = await supabase
      .from('asset_loans')
      .insert({
        asset_id: selectedAsset.id,
        owner_pta_id: member.pta_id,
        ...loanFormData,
        loan_fee: loanFormData.loan_fee || 0
      });

    if (error) {
      toast({
        title: "Error creating loan",
        description: error.message,
        variant: "destructive"
      });
    } else {
      // Update asset status
      await supabase
        .from('assets')
        .update({ status: 'on_loan' })
        .eq('id', selectedAsset.id);

      toast({
        title: "Loan created",
        description: "The loan agreement has been created.",
      });
      setLoanDialogOpen(false);
      setLoanFormData({
        borrower_type: "individual",
        borrower_pta_id: "",
        borrower_pta_name: "",
        borrower_first_name: "",
        borrower_last_name: "",
        borrower_email: "",
        borrower_phone: "",
        loan_fee: 0,
        due_date: ""
      });
      fetchAssets();
      fetchLoans();
    }
  };

  const handleReturnAsset = async (loanId: string, assetId: string) => {
    const { error } = await supabase
      .from('asset_loans')
      .update({ returned_at: new Date().toISOString() })
      .eq('id', loanId);

    if (!error) {
      await supabase
        .from('assets')
        .update({ status: 'available' })
        .eq('id', assetId);

      toast({
        title: "Asset returned",
        description: "The asset has been marked as returned.",
      });
      fetchLoans();
      fetchAssets();
    }
  };

  const handleSaveLoanSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const { data: member } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .eq('is_pta_admin', true)
      .single();

    if (!member?.pta_id) return;

    const { error } = await supabase
      .from('asset_loan_settings')
      .upsert({
        pta_id: member.pta_id,
        ...loanSettings
      }, {
        onConflict: 'pta_id'
      });

    if (error) {
      toast({
        title: "Error saving settings",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Settings saved",
        description: "Asset management settings have been updated.",
      });
    }
  };

  const handleDeleteAsset = async () => {
    if (!assetToDelete) return;

    const { error } = await supabase
      .from('assets')
      .delete()
      .eq('id', assetToDelete.id);

    if (error) {
      toast({
        title: "Error deleting asset",
        description: error.message,
        variant: "destructive"
      });
    } else {
      toast({
        title: "Asset deleted",
        description: "The asset has been removed from the catalogue.",
      });
      fetchAssets();
      setDeleteDialogOpen(false);
      setAssetToDelete(null);
    }
  };

  const handleExportCSV = () => {
    const csvContent = [
      ["Asset Name", "Category", "Location", "Value", "Status", "Loanable"],
      ...assets.map(asset => [
        asset.name,
        asset.category,
        asset.location,
        asset.value || "",
        asset.status,
        asset.loanable ? "Yes" : "No"
      ])
    ].map(row => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `assets_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    
    toast({
      title: "Export successful",
      description: "Assets have been exported to CSV"
    });
  };

  const handleCompleteAudit = async () => {
    const { data: member } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .eq('is_pta_admin', true)
      .single();

    if (!member?.pta_id) return;

    const { error } = await supabase
      .from('asset_audits')
      .insert({
        pta_id: member.pta_id,
        completed_by: user?.id
      });

    if (!error) {
      toast({
        title: "Audit recorded",
        description: "The audit has been marked as complete"
      });
      fetchAudits();
    }
  };

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = asset.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLocation = filterLocation === "all" || asset.location === filterLocation;
    const matchesStatus = filterStatus === "all" || asset.status === filterStatus;
    const matchesCategory = filterCategory === "all" || asset.category === filterCategory;
    const matchesLoanable = filterLoanable === "all" || 
      (filterLoanable === "yes" && asset.loanable) || 
      (filterLoanable === "no" && !asset.loanable);
    
    return matchesSearch && matchesLocation && matchesStatus && matchesCategory && matchesLoanable;
  });

  // Get unique categories and locations for filters
  const uniqueCategories = Array.from(new Set(assets.map(a => a.category)));
  const uniqueLocations = Array.from(new Set(assets.map(a => a.location)));

  const activeLoans = loans.filter(loan => !loan.returned_at);
  const overdueLoans = activeLoans.filter(loan => new Date(loan.due_date) < new Date());

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-4xl font-bold bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
          Assets Dashboard
        </h1>
        <p className="text-muted-foreground mt-2">
          Manage PTA assets, loans, and agreements
        </p>
      </div>

      <Tabs defaultValue="catalogue" className="w-full">
        <TabsList>
          <TabsTrigger value="catalogue">Asset Catalogue</TabsTrigger>
          {loanSettings?.loaning_enabled && <TabsTrigger value="loans">Loans</TabsTrigger>}
          <TabsTrigger value="on-loan">On Loan</TabsTrigger>
          {(auditSettings || trusteePolicies?.force_asset_audit) && <TabsTrigger value="audits">Audits</TabsTrigger>}
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Asset Catalogue Tab */}
        <TabsContent value="catalogue" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Asset Catalogue</CardTitle>
                  <CardDescription>Browse and manage PTA assets</CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleExportCSV}>
                    <FileDown className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                  <Dialog open={addAssetDialogOpen} onOpenChange={setAddAssetDialogOpen}>
                    <DialogTrigger asChild>
                      <Button>
                        <Plus className="h-4 w-4 mr-2" />
                        Add Asset
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Add New Asset</DialogTitle>
                        <DialogDescription>Register a new asset in the catalogue</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleAddAsset} className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="asset-name">Asset Name</Label>
                          <Input
                            id="asset-name"
                            placeholder="e.g., Gazebo 3x3m"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="asset-category">Category</Label>
                            <Select
                              value={formData.category}
                              onValueChange={(value) => {
                                setFormData({ ...formData, category: value });
                                setShowCustomCategory(value === "other");
                              }}
                              required
                            >
                              <SelectTrigger id="asset-category">
                                <SelectValue placeholder="Select category" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="equipment">Equipment</SelectItem>
                                <SelectItem value="audio">Audio</SelectItem>
                                <SelectItem value="furniture">Furniture</SelectItem>
                                <SelectItem value="catering">Catering</SelectItem>
                                <SelectItem value="other">Other (Custom)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="asset-value">Value (£)</Label>
                            <Input
                              id="asset-value"
                              type="number"
                              placeholder="150"
                              value={formData.value}
                              onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                            />
                          </div>
                        </div>
                        {showCustomCategory && (
                          <div className="space-y-2">
                            <Label htmlFor="custom-category">Custom Category</Label>
                            <Input
                              id="custom-category"
                              placeholder="Enter custom category"
                              value={formData.custom_category}
                              onChange={(e) => setFormData({ ...formData, custom_category: e.target.value })}
                              required
                            />
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label htmlFor="asset-location">Location</Label>
                          <Select
                            value={formData.location}
                            onValueChange={(value) => {
                              setFormData({ ...formData, location: value });
                              setShowCustomLocation(value === "other");
                            }}
                            required
                          >
                            <SelectTrigger id="asset-location">
                              <SelectValue placeholder="Select location" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="garage">Garage</SelectItem>
                              <SelectItem value="store_room">Store Room</SelectItem>
                              <SelectItem value="shipping_container">Shipping Container</SelectItem>
                              {customLocations.map(loc => (
                                <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                              ))}
                              <SelectItem value="other">Other (Custom)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {showCustomLocation && (
                          <div className="space-y-2">
                            <Label htmlFor="custom-location">Custom Location</Label>
                            <Input
                              id="custom-location"
                              placeholder="Enter custom location"
                              value={formData.custom_location}
                              onChange={(e) => setFormData({ ...formData, custom_location: e.target.value })}
                              required
                            />
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label htmlFor="asset-description">Description</Label>
                          <Textarea
                            id="asset-description"
                            placeholder="Asset details..."
                            rows={3}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          />
                        </div>
                        {loanSettings?.loaning_enabled && (
                          <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                            <div>
                              <p className="font-medium">Loanable</p>
                              <p className="text-sm text-muted-foreground">Allow this asset to be loaned out</p>
                            </div>
                            <Switch
                              checked={formData.loanable}
                              onCheckedChange={(checked) => setFormData({ ...formData, loanable: checked })}
                            />
                          </div>
                        )}
                        <div className="flex justify-end gap-2">
                          <Button type="button" variant="outline" onClick={() => setAddAssetDialogOpen(false)}>Cancel</Button>
                          <Button type="submit">Add Asset</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search assets..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <div className="grid grid-cols-4 gap-2">
                  <Select value={filterLocation} onValueChange={setFilterLocation}>
                    <SelectTrigger>
                      <SelectValue placeholder="Location" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Locations</SelectItem>
                      {uniqueLocations.map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger>
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="on_loan">On Loan</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger>
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {uniqueCategories.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={filterLoanable} onValueChange={setFilterLoanable}>
                    <SelectTrigger>
                      <SelectValue placeholder="Loanable" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Items</SelectItem>
                      <SelectItem value="yes">Loanable</SelectItem>
                      <SelectItem value="no">Not Loanable</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Asset Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Value</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAssets.map((asset) => (
                      <TableRow key={asset.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-primary" />
                            {asset.name}
                          </div>
                        </TableCell>
                        <TableCell>{asset.category}</TableCell>
                        <TableCell>{asset.location}</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            asset.status === "available" ? "bg-green-100 text-green-800" :
                            "bg-blue-100 text-blue-800"
                          }`}>
                            {asset.status === "available" ? "Available" : "On Loan"}
                          </span>
                        </TableCell>
                        <TableCell>£{asset.value || 0}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setAssetToEdit(asset);
                                setEditAssetDialogOpen(true);
                              }}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setAssetToDelete(asset);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            {asset.loanable && asset.status === "available" && (loanSettings?.loaning_enabled ?? true) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedAsset(asset);
                                  setLoanDialogOpen(true);
                                }}
                              >
                                Loan Out
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Loans Tab */}
        <TabsContent value="loans" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Active Loans</CardTitle>
              <CardDescription>Manage asset loans and agreements</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {activeLoans.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">No active loans</p>
                ) : (
                  activeLoans.map((loan) => (
                    <div key={loan.id} className={`p-4 border rounded-lg ${
                      overdueLoans.includes(loan) ? "border-destructive bg-destructive/5" : "border-border"
                    }`}>
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {overdueLoans.includes(loan) && (
                              <AlertCircle className="h-5 w-5 text-destructive" />
                            )}
                            <h4 className="font-semibold">{loan.assets?.name}</h4>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Borrower: {loan.borrower_first_name} {loan.borrower_last_name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            Due: {new Date(loan.due_date).toLocaleDateString()}
                          </p>
                          {loan.loan_fee > 0 && (
                            <p className="text-sm text-muted-foreground">
                              Fee: £{loan.loan_fee} ({loan.payment_status})
                            </p>
                          )}
                          {overdueLoans.includes(loan) && (
                            <p className="text-sm text-destructive font-medium mt-2">Overdue! Please follow up.</p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleReturnAsset(loan.id, loan.asset_id)}
                          >
                            Return Asset
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* On Loan Tab */}
        <TabsContent value="on-loan" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Items You're Borrowing</CardTitle>
              <CardDescription>Assets your PTA has borrowed from others</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-center text-muted-foreground py-8">
                No borrowed items. This feature shows assets other PTAs have loaned to you.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audits Tab */}
        <TabsContent value="audits" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Asset Audits</CardTitle>
                  <CardDescription>Track and manage asset audits</CardDescription>
                </div>
                {auditSettings && (
                  <div className="flex gap-2">
                    <Button onClick={handleCompleteAudit}>
                      <ClipboardCheck className="h-4 w-4 mr-2" />
                      Mark Audit Complete
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {!auditSettings ? (
                  <p className="text-center text-muted-foreground py-8">
                    Enable the audit system in Settings to start tracking asset audits.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <h3 className="font-semibold">Audit History</h3>
                    {audits.length === 0 ? (
                      <p className="text-muted-foreground text-sm">No audits recorded yet</p>
                    ) : (
                      audits.map(audit => (
                        <div key={audit.id} className="p-3 border rounded-lg">
                          <p className="font-medium">
                            {new Date(audit.audit_date).toLocaleDateString()}
                          </p>
                          {audit.notes && (
                            <p className="text-sm text-muted-foreground mt-1">{audit.notes}</p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Asset Management Settings</CardTitle>
              <CardDescription>Configure loan, payment, and audit options</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveLoanSettings} className="space-y-6">
                <div className="space-y-4">
                  <h4 className="font-semibold">Audit Settings</h4>
                  {trusteePolicies?.force_asset_audit && (
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        <strong>Trustee Policy Active:</strong> Asset audits are enforced and cannot be disabled.
                      </p>
                    </div>
                  )}
                  <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                    <div>
                      <p className="font-medium">Enable Audit System</p>
                      <p className="text-sm text-muted-foreground">Turn on asset audit tracking</p>
                    </div>
                    <Switch
                      checked={auditSettings || trusteePolicies?.force_asset_audit}
                      onCheckedChange={setAuditSettings}
                      disabled={trusteePolicies?.force_asset_audit}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-semibold">Loan Settings</h4>
                  <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                    <div>
                      <p className="font-medium">Enable Loaning</p>
                      <p className="text-sm text-muted-foreground">Allow assets to be loaned out</p>
                    </div>
                    <Switch
                      checked={loanSettings?.loaning_enabled || false}
                      onCheckedChange={(checked) => setLoanSettings({ ...loanSettings, loaning_enabled: checked })}
                    />
                  </div>
                </div>

                {loanSettings?.loaning_enabled && (
                  <div className="space-y-4">
                    <h4 className="font-semibold">Payment Methods</h4>
                    
                    <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div>
                        <p className="font-medium">Stripe Payments</p>
                        <p className="text-sm text-muted-foreground">Accept online payments</p>
                      </div>
                      <Switch
                        checked={loanSettings?.stripe_enabled || false}
                        onCheckedChange={(checked) => setLoanSettings({ ...loanSettings, stripe_enabled: checked })}
                      />
                    </div>

                    <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div>
                        <p className="font-medium">BACS Transfer</p>
                        <p className="text-sm text-muted-foreground">Bank transfer option</p>
                      </div>
                      <Switch
                        checked={loanSettings?.bacs_enabled || false}
                        onCheckedChange={(checked) => setLoanSettings({ ...loanSettings, bacs_enabled: checked })}
                      />
                    </div>

                    {loanSettings?.bacs_enabled && (
                      <div className="grid grid-cols-2 gap-4 ml-4">
                        <div className="space-y-2">
                          <Label>Account Number</Label>
                          <Input
                            value={loanSettings.bacs_account_number || ""}
                            onChange={(e) => setLoanSettings({ ...loanSettings, bacs_account_number: e.target.value })}
                            placeholder="12345678"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Sort Code</Label>
                          <Input
                            value={loanSettings.bacs_sort_code || ""}
                            onChange={(e) => setLoanSettings({ ...loanSettings, bacs_sort_code: e.target.value })}
                            placeholder="12-34-56"
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div>
                        <p className="font-medium">Cheque Payments</p>
                        <p className="text-sm text-muted-foreground">Accept cheque payments</p>
                      </div>
                      <Switch
                        checked={loanSettings?.cheque_enabled || false}
                        onCheckedChange={(checked) => setLoanSettings({ ...loanSettings, cheque_enabled: checked })}
                      />
                    </div>

                    {loanSettings?.cheque_enabled && (
                      <div className="space-y-4 ml-4">
                        <div className="space-y-2">
                          <Label>Payable To</Label>
                          <Input
                            value={loanSettings.cheque_payable_to || ""}
                            onChange={(e) => setLoanSettings({ ...loanSettings, cheque_payable_to: e.target.value })}
                            placeholder="PTA Name"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Postal Address</Label>
                          <Textarea
                            value={loanSettings.cheque_address || ""}
                            onChange={(e) => setLoanSettings({ ...loanSettings, cheque_address: e.target.value })}
                            placeholder="123 School Street..."
                            rows={3}
                          />
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                      <div>
                        <p className="font-medium">Cash on Collection</p>
                        <p className="text-sm text-muted-foreground">Accept cash payments</p>
                      </div>
                      <Switch
                        checked={loanSettings?.cash_enabled || false}
                        onCheckedChange={(checked) => setLoanSettings({ ...loanSettings, cash_enabled: checked })}
                      />
                    </div>

                    <div className="space-y-4">
                      <h4 className="font-semibold">Loan Agreement</h4>
                      <div className="space-y-2">
                        <Label htmlFor="loan-agreement">Agreement Text</Label>
                        <Textarea
                          id="loan-agreement"
                          value={loanSettings?.loan_agreement_text || "This agreement confirms the loan of the specified asset. The borrower agrees to return the asset by the due date in the same condition as received. Any damage or loss must be reported immediately. The borrower is responsible for the safe keeping and appropriate use of the asset during the loan period."}
                          onChange={(e) => setLoanSettings({ ...loanSettings, loan_agreement_text: e.target.value })}
                          placeholder="Enter loan agreement text..."
                          rows={6}
                          className="text-sm"
                        />
                        <p className="text-xs text-muted-foreground">This text will appear on loan agreements sent to borrowers.</p>
                      </div>
                    </div>
                  </div>
                )}

                <Button type="submit" className="w-full">
                  <SettingsIcon className="h-4 w-4 mr-2" />
                  Save Settings
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <EditAssetDialog
        asset={assetToEdit}
        open={editAssetDialogOpen}
        onOpenChange={setEditAssetDialogOpen}
        onAssetUpdated={fetchAssets}
        customLocations={customLocations}
        loaningEnabled={loanSettings?.loaning_enabled || false}
      />

      {/* Loan Dialog - Outside tabs so it works from anywhere */}
      <Dialog open={loanDialogOpen} onOpenChange={setLoanDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Create Loan Agreement</DialogTitle>
            <DialogDescription>Record a new asset loan</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateLoan} className="space-y-4">
            <div className="space-y-2">
              <Label>Borrower Type</Label>
              <Select
                value={loanFormData.borrower_type}
                onValueChange={(value) => setLoanFormData({ ...loanFormData, borrower_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual</SelectItem>
                  <SelectItem value="pta_hub">PTA Hub Member</SelectItem>
                  <SelectItem value="external_pta">External PTA</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {loanFormData.borrower_type === "pta_hub" && (
              <div className="space-y-2">
                <Label>Search PTA</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    value={ptaSearchTerm}
                    onChange={(e) => setPtaSearchTerm(e.target.value)}
                    placeholder="Type to search PTAs..."
                    className="pl-9"
                  />
                </div>
                {ptaSearchTerm && (
                  <div className="border border-border rounded-lg max-h-60 overflow-y-auto">
                    {ptas
                      .filter(pta => 
                        pta.name.toLowerCase().includes(ptaSearchTerm.toLowerCase()) ||
                        pta.school_name.toLowerCase().includes(ptaSearchTerm.toLowerCase())
                      )
                      .map(pta => (
                        <div
                          key={pta.id}
                          className={`p-3 hover:bg-accent cursor-pointer ${
                            loanFormData.borrower_pta_id === pta.id ? 'bg-accent' : ''
                          }`}
                          onClick={() => {
                            setLoanFormData({ ...loanFormData, borrower_pta_id: pta.id });
                            setPtaSearchTerm(`${pta.name} - ${pta.school_name}`);
                          }}
                        >
                          <p className="font-medium">{pta.name}</p>
                          <p className="text-sm text-muted-foreground">{pta.school_name}</p>
                        </div>
                      ))
                    }
                    {ptas.filter(pta => 
                      pta.name.toLowerCase().includes(ptaSearchTerm.toLowerCase()) ||
                      pta.school_name.toLowerCase().includes(ptaSearchTerm.toLowerCase())
                    ).length === 0 && (
                      <div className="p-3 text-center text-muted-foreground">
                        No PTAs found
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {loanFormData.borrower_type === "external_pta" && (
              <div className="space-y-2">
                <Label>PTA Name</Label>
                <Input
                  value={loanFormData.borrower_pta_name}
                  onChange={(e) => setLoanFormData({ ...loanFormData, borrower_pta_name: e.target.value })}
                  placeholder="Enter PTA name"
                  required
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name</Label>
                <Input
                  value={loanFormData.borrower_first_name}
                  onChange={(e) => setLoanFormData({ ...loanFormData, borrower_first_name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name</Label>
                <Input
                  value={loanFormData.borrower_last_name}
                  onChange={(e) => setLoanFormData({ ...loanFormData, borrower_last_name: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={loanFormData.borrower_email}
                  onChange={(e) => setLoanFormData({ ...loanFormData, borrower_email: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={loanFormData.borrower_phone}
                  onChange={(e) => setLoanFormData({ ...loanFormData, borrower_phone: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Loan Fee (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={loanFormData.loan_fee}
                  onChange={(e) => setLoanFormData({ ...loanFormData, loan_fee: parseFloat(e.target.value) || 0 })}
                />
              </div>
              <div className="space-y-2">
                <Label>Due Date</Label>
                <Input
                  type="date"
                  value={loanFormData.due_date}
                  onChange={(e) => setLoanFormData({ ...loanFormData, due_date: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setLoanDialogOpen(false)}>Cancel</Button>
              <Button type="submit">
                <FileText className="h-4 w-4 mr-2" />
                Create Loan
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{assetToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteAsset} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}